import { Bus as MixerBus } from '@/audio/Bus'
import {
  useSequencerStore,
  selectCurrentBuses,
  MASTER_BUS_ID,
} from '@/stores/sequencerStore'

/**
 * Runtime registry of mixer `Bus` instances keyed by the id used in
 * the sequencer store. Lives in its own module (not useAudioEngine)
 * to avoid a circular import: sequencerStore.ts imports types from
 * useAudioEngine, and useAudioEngine needs the store to resolve
 * pad → bus routing. Placing the registry here keeps the two
 * modules decoupled.
 *
 * P5b extends this module to own the multi-channel output graph:
 *
 *   userBus (ch > 0) ──┐
 *                      ├─ splitter(2) → ChannelMergerNode(32)[ch, ch+1]
 *   userBus (ch > 0) ──┤
 *                      │
 *   userBus (ch = 0) → masterFxInput → masterFx out → splitter(2) ──┐
 *                                                                    ├→ merger[0, 1]
 *                                                                    │
 *                                                          merger → finalDestination
 *
 * Buses with `outputChannelStart === 0` (default) go through the
 * master FX chain like before; user buses with a non-zero start
 * BYPASS the master FX so per-hardware-channel sends stay clean
 * (typical use case: ch 3/4 is a monitor feed or an outboard-
 * processing pre-send). Channel-count clamping is the UI's job —
 * the engine happily addresses any slot in merger's 32-channel
 * output; channels beyond the device's capacity are silently
 * dropped by the browser / CoreAudio layer at the destination.
 */

/** Merger channel count — bounded above by what hardware + stack
 *  can reasonably expose. 32 covers every USB interface we've
 *  tested and matches the ceiling of CoreAudio's default stream
 *  descriptor, with room left over for virtual routes (Loopback,
 *  BlackHole 16ch ×2). Anything larger would waste a pile of
 *  silent slots per context. */
const MERGER_CHANNEL_COUNT = 32

interface UserBusEntry {
  bus: MixerBus
  /** Splitter inserted between `bus.output` and the merger so
   *  stereo channels map onto merger slots individually. Null when
   *  the bus routes through master FX (ch 0), since in that case
   *  the bus connects directly to masterInput and master FX's
   *  own splitter handles the merger hand-off. */
  splitter: ChannelSplitterNode | null
  /** Current channelStart applied by the engine. Tracked so
   *  syncBuses can short-circuit when the store hasn't changed. */
  currentChannelStart: number
}

interface RegistryState {
  ctx: AudioContext
  /** Destination for any user bus routed through master FX (ch 0).
   *  I.e. the master FX bus input node. */
  masterInput: AudioNode | null
  /** ChannelMergerNode(MERGER_CHANNEL_COUNT) with discrete
   *  interpretation — every stereo source lands on a specific pair
   *  with no auto down-mixing. */
  merger: ChannelMergerNode
  /** Entry point for the master FX bus output. Splits stereo and
   *  routes channel 0 → merger[0], channel 1 → merger[1]. */
  masterSlot: AudioNode
  /** User buses keyed by store id. */
  userBuses: Map<string, UserBusEntry>
  /** Dedicated `MixerBus` that owns plugin-kind effects on the
   *  Master bus (FFT Analyzer, future LO-FI, etc.). Sits AFTER the
   *  legacy FxBank (filter/delay/reverb/bitcrush/stutter) so master
   *  plugins see the fully-mastered signal. Before this field
   *  existed, `container.buses[MASTER_BUS_ID].effects` was written
   *  by the Inspector but never instantiated in the audio graph —
   *  the Analyzer rendered silent. */
  masterPluginBus: MixerBus | null
  /** Guards the store-subscription setup. */
  subscribed: boolean
}

let registry: RegistryState | null = null

/**
 * Build the channel merger graph and wire it to the caller-supplied
 * final destination (the OutputSink's input node in live playback).
 * Returns a stereo entry point that the master FX chain should
 * terminate on, so master FX lands on merger channels [0, 1].
 *
 * Call order in `useAudioEngine.ensureBus`:
 *   1. `ensureBusOutputRouter(ctx, sinkInput)` → masterSlot
 *   2. `createFxBus(ctx, masterSlot)` → master FX instance
 *   3. `ensureBusRegistry(ctx, sharedBus.input)` → user bus graph
 */
export function ensureBusOutputRouter(
  ctx: AudioContext,
  finalDest: AudioNode,
): AudioNode {
  if (registry) return registry.masterSlot
  const merger = ctx.createChannelMerger(MERGER_CHANNEL_COUNT)
  merger.channelInterpretation = 'discrete'
  merger.connect(finalDest)
  const masterSlot = ctx.createGain()
  const masterSplitter = ctx.createChannelSplitter(2)
  masterSlot.connect(masterSplitter)
  masterSplitter.connect(merger, 0, 0)
  masterSplitter.connect(merger, 1, 1)
  registry = {
    ctx,
    masterInput: null,
    merger,
    masterSlot,
    userBuses: new Map(),
    masterPluginBus: null,
    subscribed: false,
  }
  return masterSlot
}

/**
 * Prepare the registry for the given context. Called once by the
 * audio engine after the master FX bus exists; subsequent calls
 * are no-ops (the registry is a singleton for the session).
 *
 * Sets up a store subscription so store-side bus edits (add/remove,
 * volume/pan, outputChannelStart) are mirrored into the runtime
 * Bus instances without the engine having to poll.
 */
export function ensureBusRegistry(
  ctx: AudioContext,
  masterInput: AudioNode,
): void {
  if (!registry) {
    // Somebody wired bus registry without preparing the output
    // router. Historically this path was the only one; we preserve
    // stereo-only behavior by synthesising a router that routes
    // directly to masterInput. Nothing downstream needs to change
    // when the caller eventually upgrades to P5b.
    registry = {
      ctx,
      masterInput,
      merger: ctx.createChannelMerger(MERGER_CHANNEL_COUNT),
      masterSlot: masterInput,
      userBuses: new Map(),
      masterPluginBus: null,
      subscribed: false,
    }
    registry.merger.channelInterpretation = 'discrete'
  } else {
    registry.masterInput = masterInput
  }
  // Lazily build the master plugin bus — owns the plugin-kind effect
  // chain on `container.buses[MASTER_BUS_ID].effects`. The bus's
  // `.output` must already be patched into the graph by
  // `ensureBus()` in `useAudioEngine.ts`, which calls
  // `registerMasterPluginBus` before syncBuses runs for the first
  // time. If the caller hasn't registered one, syncBuses treats
  // master effects as no-ops (backwards-compat).
  syncBuses()
  if (!registry.subscribed) {
    registry.subscribed = true
    useSequencerStore.subscribe(syncBuses)
  }
}

/**
 * Return the AudioNode that voices for a given bus id should
 * connect to. Unknown ids (no bus exists, or bus was deleted mid-
 * schedule) fall back to the master input so hits stay audible.
 */
export function resolveBusInput(busId: string | undefined): AudioNode | null {
  if (!registry) return null
  if (!busId || busId === MASTER_BUS_ID) return registry.masterInput
  const entry = registry.userBuses.get(busId)
  return entry ? entry.bus.input : registry.masterInput
}

/**
 * Expose the live ChannelMergerNode + its channel count so callers
 * (AudioInput, test harnesses) can connect signal directly to
 * specific hardware channel slots, bypassing the bus graph. Returns
 * null if the registry hasn't been initialised — callers should
 * guard.
 */
export function getBusOutputMerger(): {
  merger: ChannelMergerNode
  channelCount: number
} | null {
  if (!registry) return null
  return { merger: registry.merger, channelCount: MERGER_CHANNEL_COUNT }
}

/**
 * Look up the live `Bus` instance for a given store id. Returns
 * null for any id that doesn't resolve (unknown or deleted bus).
 * Master now resolves to `masterPluginBus` (registered by
 * `ensureBus` in useAudioEngine.ts) so the Plugin-tab spectrum
 * view can reach the master FFT Analyzer's handle + analyzer id.
 */
export function getUserBus(busId: string): MixerBus | null {
  if (!registry) return null
  if (busId === MASTER_BUS_ID) return registry.masterPluginBus
  const entry = registry.userBuses.get(busId)
  return entry?.bus ?? null
}

/**
 * Register the `MixerBus` that owns plugin-kind effects on the
 * master bus. Called once from `ensureBus` in `useAudioEngine.ts`
 * at engine boot, between the master FX bank and the output
 * merger slot. Running `syncBuses` immediately after registration
 * seeds the bus's initial effect list from the current store
 * snapshot; subsequent store changes flow through the existing
 * subscription.
 */
export function registerMasterPluginBus(bus: MixerBus): void {
  if (!registry) return
  registry.masterPluginBus = bus
  syncBuses()
}

/** Sync the current container's buses with the runtime registry.
 *  Adds missing buses, removes orphaned ones, re-applies volume /
 *  pan / output-channel routing for existing ones. Called on every
 *  store change. */
function syncBuses(): void {
  if (!registry) return
  const state = useSequencerStore.getState()
  const buses = selectCurrentBuses(state)
  const seen = new Set<string>()
  for (const def of buses) {
    seen.add(def.id)
    if (def.id === MASTER_BUS_ID) {
      // Master bus plugin effects live on `masterPluginBus` — a
      // `MixerBus` that sits between the legacy FxBank and the
      // output merger slot (see `ensureBus` in useAudioEngine.ts).
      // `setEffects` diffs by id+kind so param tweaks don't rebuild
      // the whole chain. Volume / pan on master are still owned by
      // the legacy FxBank path, so we skip them here.
      if (registry.masterPluginBus) {
        registry.masterPluginBus.setEffects(def.effects ?? [])
      }
      continue
    }
    let entry = registry.userBuses.get(def.id)
    if (!entry) {
      const bus = new MixerBus(registry.ctx, def.id)
      entry = { bus, splitter: null, currentChannelStart: -1 }
      registry.userBuses.set(def.id, entry)
    }
    // Apply latest volume + pan. `setVolume` / `setPan` ramp so
    // rapid slider drags don't zipper; idempotent when the value
    // hasn't changed because the target equals the current value.
    entry.bus.setVolume(def.volume)
    entry.bus.setPan(def.pan)
    // Reconcile per-bus FX chain. Bus.setEffects diffs by effect
    // id + kind so a param tweak doesn't rebuild the whole chain.
    entry.bus.setEffects(def.effects ?? [])

    // Output channel routing. Rewire only when the selected slot
    // actually changed — re-building the splitter on every store
    // tick would introduce audible pops on slider drags elsewhere
    // in the UI.
    const desiredStart = def.outputChannelStart ?? 0
    if (entry.currentChannelStart !== desiredStart) {
      rewireUserBus(registry, entry, desiredStart)
      entry.currentChannelStart = desiredStart
    }
  }
  // Remove any runtime bus that's no longer in the store. Its
  // connections get torn down so audio stops flowing through it.
  for (const [id, entry] of registry.userBuses) {
    if (!seen.has(id)) {
      if (entry.splitter) {
        try {
          entry.splitter.disconnect()
        } catch {
          /* already disconnected */
        }
      }
      entry.bus.dispose()
      registry.userBuses.delete(id)
    }
  }
}

/** Re-route a user bus to either the master FX input (when the
 *  store has channelStart === 0) or directly to the merger at
 *  [start, start+1]. Tears down the previous routing cleanly to
 *  avoid duplicate connections. */
function rewireUserBus(
  reg: RegistryState,
  entry: UserBusEntry,
  channelStart: number,
): void {
  // Tear down the previous splitter hand-off, if any.
  if (entry.splitter) {
    try {
      entry.splitter.disconnect()
    } catch {
      /* already disconnected */
    }
    entry.splitter = null
  }
  if (channelStart <= 0) {
    // Default path — route through master FX like pre-P5b.
    if (reg.masterInput) entry.bus.connectTo(reg.masterInput)
    return
  }
  // Multi-channel path — bypass master FX, send stereo directly to
  // merger channels [start, start+1]. Channel indices are clamped
  // at `MERGER_CHANNEL_COUNT - 1` so the connection call never
  // throws even if the user picks a slot beyond the hardware's
  // capacity (the browser still silently drops the slot).
  const splitter = reg.ctx.createChannelSplitter(2)
  const left = Math.min(channelStart, MERGER_CHANNEL_COUNT - 2)
  const right = left + 1
  entry.bus.connectTo(splitter)
  splitter.connect(reg.merger, 0, left)
  splitter.connect(reg.merger, 1, right)
  entry.splitter = splitter
}
